import _jsx from '@babel/runtime/helpers/jsx';
import _extends from '@babel/runtime/helpers/extends';
import React, { createElement, forwardRef } from 'react';
import { oneOfType, shape, string, func, node, bool, arrayOf, number } from 'prop-types';
import classNames from 'classnames';
import { trackEvent, backgroundColor, hyphenatedbem } from '@vpp-frontend-components/common';
import { useVppContext } from '@vpp-frontend-components/vpp-context';
import { StyledLabel } from '@vpp-frontend-components/styled-label';
import { Skeleton } from '@vpp-frontend-components/placeholder';
import { Icon } from '@vpp-frontend-components/icon';
import { componentEnhance } from '@vpp-frontend-components/component-enhance';
import AndesModal from '@andes/modal';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
const namespace$1 = 'ui-pdp-media';
const MediaAction = _ref => {
  let {
    target = null,
    id = null,
    role = 'link',
    htmlTarget = '_self',
    className = '',
    onClick = null,
    onKeyDown = null,
    label,
    title = null,
    track = null,
    accessibilityText = null,
    num_font_size = null,
    modal = null
  } = _ref;
  const [showModal, toggleModalVisibility] = React.useState(false);
  const handleQuestionAction = e => {
    e.preventDefault();
    toggleModalVisibility(!showModal);
  };
  const baseHandlerAction = e => {
    if (track) {
      trackEvent(track);
    }
    if (modal) {
      handleQuestionAction(e);
    }
  };
  const onKeyDownHandler = e => {
    baseHandlerAction(e);
    if (onKeyDown) {
      onKeyDown(e);
    }
  };
  const onClickHandler = e => {
    baseHandlerAction(e);
    if (onClick) {
      onClick(e);
    }
  };
  const elementType = role === 'link' ? 'a' : 'button';
  const elementProps = {
    title,
    href: target,
    role: target ? null : role,
    target: htmlTarget,
    tabindex: !target && (role === 'link' || role === 'button') ? 0 : null,
    id,
    onClick: onClickHandler,
    onKeyDown: onKeyDownHandler,
    className: classNames(`${namespace$1}__action`, className)
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/createElement(elementType, elementProps, /*#__PURE__*/React.createElement(React.Fragment, null, componentEnhance.jsx(label.text, label.values, null, num_font_size), accessibilityText && /*#__PURE__*/_jsx("span", {
    className: "ui-pdp--hide"
  }, void 0, accessibilityText))), modal && /*#__PURE__*/_jsx(AndesModal, {
    title: modal === null || modal === void 0 ? void 0 : modal.title,
    className: classNames(`${namespace$1}__modal`, className),
    closeButtonSrLabel: "Cerrar",
    open: showModal,
    onClose: () => toggleModalVisibility(false)
  }, void 0, /*#__PURE__*/_jsx("iframe", {
    style: {
      width: '100%',
      height: '100%'
    },
    id: "modal-iframe",
    title: modal === null || modal === void 0 ? void 0 : modal.title,
    scrolling: "no",
    src: modal === null || modal === void 0 ? void 0 : modal.url
  })));
};
var _Skeleton, _Fragment;
const namespace = 'ui-pdp-media';
const renderMediaAction = action => /*#__PURE__*/React.isValidElement(action) ? action : /*#__PURE__*/React.createElement(MediaAction, _extends({
  htmlTarget: action.html_target
}, action));
const MediaForwardRef = (_ref, ref) => {
  let {
    action = null,
    children = null,
    className = '',
    figure = null,
    icon = null,
    subtitles = null,
    title = null,
    isFetching = false,
    showChevron = false,
    tooltip = null,
    background_color = null,
    num_font_size,
    accessibilityText,
    tag = 'p'
  } = _ref;
  const {
    staticProps,
    dependencies
  } = useVppContext();
  const {
    TooltipWrapper
  } = dependencies;
  const {
    lowEnd,
    deviceType
  } = staticProps;
  if (isFetching && deviceType === 'mobile') {
    return _Skeleton || (_Skeleton = /*#__PURE__*/_jsx(Skeleton, {}));
  }
  const textIcon = (title === null || title === void 0 ? void 0 : title.text_icon) && /*#__PURE__*/React.createElement(Icon, title.text_icon);
  const chevron = _Fragment || (_Fragment = /*#__PURE__*/React.createElement(React.Fragment, null, "\xA0", /*#__PURE__*/_jsx(Icon, {
    id: "chevron"
  })));
  let icons = null;
  if (title !== null && title !== void 0 && title.text_icon && showChevron) {
    icons = /*#__PURE__*/_jsx("span", {
      className: `${namespace}__title-icons`
    }, void 0, textIcon, chevron);
  } else if (title !== null && title !== void 0 && title.text_icon) {
    icons = textIcon;
  } else if (showChevron) {
    icons = chevron;
  }
  let finalTitle = title;
  if (title !== null && title !== void 0 && title.text) {
    finalTitle = title.text;
  }
  if (title !== null && title !== void 0 && title.text_icon && !title.text) {
    finalTitle = null;
  }
  return /*#__PURE__*/React.createElement("div", {
    className: classNames(namespace, className, backgroundColor(background_color)),
    ref: ref
  }, figure && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/_jsx("figure", {
    "aria-hidden": "true",
    className: `${namespace}__figure`
  }, void 0, figure), accessibilityText && /*#__PURE__*/_jsx("span", {
    className: "andes-visually-hidden"
  }, void 0, accessibilityText)), icon && /*#__PURE__*/React.createElement(Icon, _extends({}, icon, {
    className: `${namespace}__icon`
  })), /*#__PURE__*/_jsx("div", {
    className: `${namespace}__body`
  }, void 0, /*#__PURE__*/_jsx(TooltipWrapper, {
    className: className,
    tooltip: tooltip
  }, void 0, title && /*#__PURE__*/_jsx(StyledLabel, {
    className: classNames({
      [`${namespace}__title--plain`]: title.plain,
      [`${namespace}__title`]: !title.plain,
      [`${namespace}__title--on-hover`]: title.onHover
    }),
    font_family: title.font_family,
    font_size: title.font_size,
    color: title.color,
    as: tag,
    text: /*#__PURE__*/React.createElement(React.Fragment, null, finalTitle && componentEnhance.jsx(finalTitle, title.values, null, num_font_size), icons && /*#__PURE__*/React.createElement(React.Fragment, null, finalTitle !== null && ' ', icons))
  })), subtitles === null || subtitles === void 0 ? void 0 : subtitles.filter(Boolean).map(subtitle => /*#__PURE__*/React.createElement(StyledLabel, _extends({
    className: `${namespace}__text`,
    key: subtitle.text,
    as: "p"
  }, subtitle, {
    text: componentEnhance.jsx(subtitle.text, subtitle.values)
  }))), children, !lowEnd && action && renderMediaAction(action)));
};
const Media = /*#__PURE__*/forwardRef(MediaForwardRef);
Media.propTypes = {
  action: oneOfType([shape({
    label: shape({
      text: string.isRequired,
      color: string
    }).isRequired,
    onClick: func,
    target: string.isRequired
  }), node, func]),
  children: node,
  className: string,
  figure: oneOfType([node, func]),
  isFetching: bool,
  subtitles: arrayOf(shape({
    text: string,
    color: string,
    values: shape({
      price: shape({
        type: string,
        value: number,
        original_value: number,
        currency_symbol: string
      })
    })
  })),
  showChevron: bool,
  title: oneOfType([shape({
    text: string.isRequired,
    values: shape({}),
    text_icon: shape({
      id: string.isRequired
    })
  }), string]),
  tooltip: shape({
    side: string,
    content: node
  }),
  icon: shape({}),
  background_color: string,
  num_font_size: number,
  accessibilityText: string,
  tag: string
};
const mediaToProps = props => _objectSpread(_objectSpread({}, props), {}, {
  className: hyphenatedbem(props.id),
  title: props.title ? _objectSpread(_objectSpread({}, props.title), {}, {
    plain: true
  }) : null
});
export { Media, MediaAction, mediaToProps };
